import { useImagePreview } from '@/hooks/useImagePreview';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

interface ModelPreviewProps {
  modelId: string | null;
  alt?: string;
  disableGif?: boolean;
}

export const ModelPreview: FC<ModelPreviewProps> = ({ modelId, alt = 'Model preview', disableGif = false }) => {
  const [pngLoaded, setPngLoaded] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || isInViewport) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '250px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isInViewport]);

  const { pngUrl, gifUrl, isHovered, isLoading, handleMouseEnter, handleMouseLeave } = useImagePreview({
    modelId,
    disableGif,
    enabled: isInViewport,
  });

  if (!isInViewport || isLoading) {
    return <div ref={containerRef} className="w-full h-full bg-gray-200 animate-pulse" />;
  }

  if (!pngUrl) {
    return (
      <div ref={containerRef} className="w-full h-full bg-gray-200 flex items-center justify-center">
        <p className="text-sm text-gray-500">No preview available</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      onMouseEnter={disableGif ? undefined : handleMouseEnter}
      onMouseLeave={disableGif ? undefined : handleMouseLeave}
    >
      {/* Pulse placeholder shown only until the PNG first loads */}
      {!pngLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}

      {/* Base layer: PNG — src is set once and never reassigned */}
      <img
        src={pngUrl}
        alt={alt}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity ${pngLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setPngLoaded(true)}
        onError={() => setPngLoaded(true)}
        loading="lazy"
      />

      {/* Overlay layer: GIF — rendered only once gifUrl is available; src never changes.
          Visibility is controlled purely by CSS opacity on hover. */}
      {gifUrl && (
        <img
          src={gifUrl}
          alt={alt}
          aria-hidden={true}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
        />
      )}
    </div>
  );
};
